import React, { useEffect, useState, useMemo } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react'
import { useTranslation } from '../hooks/use-i18n'
import formula1Service from '../services/formula1Service'
import {
  getMyPublicEnrollments,
  getMyPublicFormula1Predictions,
  submitPublicFormula1Prediction,
} from '../services/publicService'
import type { Formula1Race } from '../types/formula1'
import type { PublicParticipant } from '../types/public'
import Formula1ExistedPositionModal from '../components/Formula1ExistedPositionModal'
import type { DriverOption } from '../components/Formula1SelectPositionModal'
import toast from 'react-hot-toast'

const POSITIONS = 10

const normalizeRace = (r: Formula1Race | any): Formula1Race => {
  const race = { ...r }
  if (typeof race.drivers === 'string') {
    try { race.drivers = JSON.parse(race.drivers) } catch { race.drivers = [] }
  }
  if (typeof race.cars === 'string') {
    try { race.cars = JSON.parse(race.cars) } catch { race.cars = [] }
  }
  if (!Array.isArray(race.drivers)) race.drivers = []
  if (!Array.isArray(race.cars)) race.cars = []
  return race
}

const PublicFormula1PlayPage: React.FC = () => {
  const { raceId } = useParams<{ raceId: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [race, setRace] = useState<Formula1Race | null>(null)
  const [enrollment, setEnrollment] = useState<PublicParticipant | null>(null)
  const [isLoadingPage, setIsLoadingPage] = useState(true)
  const [selectedPositionIndex, setSelectedPositionIndex] = useState(0)
  const [positionToDriver, setPositionToDriver] = useState<Record<number, string>>({})
  const [existingPrediction, setExistingPrediction] = useState<{ predictedPositions: Record<string, number> } | null>(null)
  const [isLoadingPrediction, setIsLoadingPrediction] = useState(false)
  const [editingPosition, setEditingPosition] = useState<number | null>(null)
  const [showSelectModal, setShowSelectModal] = useState(false)
  const [showExistedModal, setShowExistedModal] = useState(false)
  const [, setIsSaving] = useState(false)

  const currentPosition = selectedPositionIndex + 1

  useEffect(() => {
    if (!raceId) return
    let cancelled = false
    setIsLoadingPage(true)
    Promise.all([
      formula1Service.getRace(raceId),
      getMyPublicEnrollments(),
    ])
      .then(([raceData, enrollments]) => {
        if (cancelled) return
        const r = normalizeRace(raceData)
        setRace(r)
        const f1 = enrollments.find((e) => (e as any).gameType === 'formula1' || e.publicTournament?.gameType === 'formula1')
        setEnrollment(f1 ?? null)
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error('Failed to load race')
          setRace(null)
          setEnrollment(null)
        }
      })
      .finally(() => { if (!cancelled) setIsLoadingPage(false) })
    return () => { cancelled = true }
  }, [raceId])

  useEffect(() => {
    if (!enrollment?.id || !raceId) return
    let cancelled = false
    setIsLoadingPrediction(true)
    setPositionToDriver({})
    setExistingPrediction(null)
    setSelectedPositionIndex(0)
    setEditingPosition(null)
    getMyPublicFormula1Predictions(enrollment.id)
      .then((list) => {
        if (cancelled) return
        const pred = list.find((p) => p.raceId === raceId || (p as any).race?.id === raceId)
        if (pred?.predictedPositions) {
          setExistingPrediction(pred)
          const map: Record<number, string> = {}
          Object.entries(pred.predictedPositions).forEach(([driver, position]) => {
            map[Number(position)] = driver
          })
          setPositionToDriver(map)
        }
      })
      .catch(() => { if (!cancelled) setIsLoadingPrediction(false) })
      .finally(() => { if (!cancelled) setIsLoadingPrediction(false) })
    return () => { cancelled = true }
  }, [enrollment?.id, raceId])

  useEffect(() => {
    if (isLoadingPrediction || !race) return
    if (selectedPositionIndex >= POSITIONS) return
    const hasExisting = positionToDriver[currentPosition]
    if (hasExisting && editingPosition !== currentPosition) {
      setShowExistedModal(true)
      setShowSelectModal(false)
    } else {
      setShowExistedModal(false)
      setShowSelectModal(true)
    }
  }, [selectedPositionIndex, positionToDriver, currentPosition, editingPosition, isLoadingPrediction, race])

  const availableDriversForPosition = useMemo((): DriverOption[] => {
    if (!race) return []
    const drivers = Array.isArray(race.drivers) ? race.drivers : []
    const cars = Array.isArray(race.cars) ? race.cars : []
    const selectedElsewhere = Object.entries(positionToDriver)
      .filter(([pos]) => Number(pos) !== currentPosition)
      .map(([, driver]) => driver)
    const currentDriver = positionToDriver[currentPosition]
    return drivers
      .map((d, i) => ({ label: d, value: d, car: cars[i] || 'Unknown' }))
      .filter((opt) => opt.value === currentDriver || !selectedElsewhere.includes(opt.value))
  }, [race, positionToDriver, currentPosition])

  const effectiveTournamentId =
    enrollment?.participantTournaments?.find((pt) => (pt as any).publicTournament?.gameType === 'formula1')?.publicTournamentId ??
    enrollment?.participantTournaments?.[0]?.publicTournamentId ??
    enrollment?.publicTournamentId

  const savePrediction = async (nextPositionToDriver: Record<number, string>) => {
    if (!enrollment?.id || !effectiveTournamentId || !raceId) return
    if (race?.status === 'completed') {
      toast.error(t('publicFormula1.predictionsClosed') || 'Predictions are closed for this race.')
      return
    }
    const predictedPositions: Record<string, number> = {}
    Object.entries(nextPositionToDriver).forEach(([pos, driver]) => {
      predictedPositions[driver] = Number(pos)
    })
    try {
      setIsSaving(true)
      await submitPublicFormula1Prediction(effectiveTournamentId, {
        publicParticipantId: enrollment.id,
        raceId,
        predictedPositions,
      })
      setExistingPrediction({ predictedPositions })
      toast.success(t('formula1.predictionSaved'))
    } catch (err: any) {
      const msg = err.response?.data?.message
      const text = Array.isArray(msg) ? msg.join(' ') : msg || 'Failed to save prediction'
      toast.error(text)
      // Do not rethrow: avoid "Uncaught runtime errors" overlay; user already sees the toast
    } finally {
      setIsSaving(false)
    }
  }

  const handleSelectDriver = async (driver: string) => {
    const next = { ...positionToDriver, [currentPosition]: driver }
    setPositionToDriver(next)
    setShowSelectModal(false)
    setEditingPosition(null)
    if (Object.keys(next).length === POSITIONS) {
      await savePrediction(next)
    }
    setSelectedPositionIndex((i) => Math.min(i + 1, POSITIONS))
  }

  const handleKeepPosition = () => {
    setShowExistedModal(false)
    setSelectedPositionIndex((i) => Math.min(i + 1, POSITIONS))
  }

  const handleChangePosition = () => {
    setShowExistedModal(false)
    setEditingPosition(currentPosition)
    setShowSelectModal(true)
  }

  const completedAllForRace = selectedPositionIndex >= POSITIONS

  if (isLoadingPage) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    )
  }

  if (!race || !enrollment) {
    return (
      <div className="min-h-screen bg-background p-4 sm:p-6">
        <div className="max-w-2xl mx-auto">
          <Link to="/public/formula1" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 sm:mb-6">
            <ArrowLeft size={18} /> {t('dashboard.backToDashboard')}
          </Link>
          <div className="glass-card rounded-2xl p-6 text-center">
            <p className="text-foreground">{t('publicFormula1.raceNotFoundOrNotEnrolled')}</p>
            <Link to="/public/formula1" className="btn-primary mt-4 inline-block px-4 py-2 rounded-xl">{t('publicFormula1.backToPublicF1')}</Link>
          </div>
        </div>
      </div>
    )
  }

  if (completedAllForRace) {
    return (
      <div className="min-h-screen bg-background p-4 sm:p-6">
        <div className="max-w-2xl mx-auto">
          <div className="blue-sky-card rounded-2xl p-6 sm:p-8 text-center">
            <div className="text-6xl mb-6">🎉</div>
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">
              {t('formula1.completedAllPredictions')}
            </h2>
            <p className="text-white/80 text-small sm:text-body mb-6">
              {t('formula1.completedAllPredictionsDesc')}
            </p>
            <button
              onClick={() => navigate('/public/formula1')}
              className="px-6 py-3 bg-primary hover:opacity-90 text-primary-foreground rounded-xl font-semibold"
            >
              Back to Public Formula 1
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background p-4 sm:p-6">
      <div className="max-w-2xl mx-auto">
        <Link
          to="/public/formula1"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 sm:mb-6"
        >
          <ArrowLeft size={18} /> {t('publicFormula1.backToPublicF1')}
        </Link>

        <div className="blue-sky-card rounded-2xl p-6 sm:p-8">
          <div className="flex items-center justify-between w-full mb-6">
            <div className="flex gap-1 sm:gap-1.5">
              {Array.from({ length: POSITIONS }).map((_, i) => (
                <div
                  key={i}
                  className={`h-2 w-4 sm:w-5 rounded-sm flex-shrink-0 ${
                    i < selectedPositionIndex ? 'bg-primary' : i === selectedPositionIndex ? 'bg-primary/80' : 'bg-white/20'
                  }`}
                />
              ))}
            </div>
            <span className="text-white/80 font-semibold text-small sm:text-body ml-2">
              {currentPosition}/{POSITIONS}
            </span>
          </div>

          <div className="mb-4">
            <span className="text-4xl sm:text-5xl inline-block mb-2">🏎️</span>
            <h1 className="text-xl sm:text-2xl font-bold text-white">
              {t('formula1.playingRoomPredictions')}
            </h1>
          </div>

          <p className="text-white/80 text-small sm:text-body mb-6">
            Race {race.day}: {race.city}
            {race.raceDate && (
              <span className="ml-2">· {new Date(race.raceDate).toLocaleDateString()}</span>
            )}
          </p>

          <div className="flex items-center justify-center gap-4 mt-6">
            <button
              type="button"
              onClick={() => setSelectedPositionIndex((i) => Math.max(0, i - 1))}
              disabled={selectedPositionIndex === 0}
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40 flex items-center justify-center text-white"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-white font-semibold">
              {currentPosition === 1 && '1st'}
              {currentPosition === 2 && '2nd'}
              {currentPosition === 3 && '3rd'}
              {currentPosition > 3 && `${currentPosition}th`} place
            </span>
            <button
              type="button"
              onClick={() => setSelectedPositionIndex((i) => Math.min(POSITIONS - 1, i + 1))}
              disabled={selectedPositionIndex >= POSITIONS - 1}
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40 flex items-center justify-center text-white"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {showSelectModal && !showExistedModal && (
          <div className="mt-5 space-y-2">
            <p className="text-sm text-muted-foreground px-1">{t('formula1.selectOneDriver')}</p>
            {availableDriversForPosition.map((driver) => (
              <button
                key={driver.value}
                type="button"
                onClick={() => handleSelectDriver(driver.value)}
                className="w-full px-4 py-3.5 bg-sky-500/25 hover:bg-sky-500/40 text-foreground rounded-xl border border-white/10 font-semibold text-left flex items-center justify-between gap-2"
              >
                <span className="truncate">{driver.label}</span>
                <span className="text-muted-foreground text-xs shrink-0">({driver.car})</span>
              </button>
            ))}
          </div>
        )}

        <Formula1ExistedPositionModal
          isOpen={showExistedModal && !!positionToDriver[currentPosition]}
          position={currentPosition}
          driverName={positionToDriver[currentPosition] ?? ''}
          onKeepClick={handleKeepPosition}
          onChangeClick={handleChangePosition}
          canChange={race?.status !== 'completed'}
        />
      </div>
    </div>
  )
}

export default PublicFormula1PlayPage
